import React from 'react';

import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Bar from './Bar';
import Gallery from './Gallery';

function PhotoGallery({
  selectedIndex,
  options,
  onDownload,
  onDelete,
  barProps,
  withBar,
  withArrows,
  isSolo,
  onClose,
  onSelect,
  galleryProps,
}) {
  const [form, setForm] = React.useState({
    options,
    selectedIndex,
    selectedIndexHistory: undefined,
  });

  const hasValue = form?.options?.[form?.selectedIndex];
  const isFirstIndex = form?.selectedIndex <= 0;
  const isLastIndex = form?.options?.length === form?.selectedIndex + 1;

  const handleOnArrowsClick = (type) => {
    setForm((prevState) => {
      let v = null;

      if (type === 'LEFT') {
        v = form?.selectedIndex - 1;
      } else {
        v = form?.selectedIndex + 1;
      }

      return {
        ...prevState,
        selectedIndex: v,
      };
    });
  };

  const handleOnSelect = (index) => {
    setForm((prevState) => ({
      ...prevState,
      selectedIndex: index,
    }));
  };

  const handleOnViewGallery = () => {
    setForm((prevState) => ({
      ...prevState,
      selectedIndexHistory: prevState?.selectedIndex,
      selectedIndex: null,
    }));
  };

  const handleOnBarSelect = () => {
    if (!barProps?.withSelect) return;

    onSelect(form);
  };

  React.useEffect(() => {
    setForm({
      options,
      selectedIndex,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(options), selectedIndex]);

  if (!hasValue) {
    return (
      <>
        <div className="fixed -bottom-44 left-0 w-full h-[300px] bg-gradient-to-t from-[#373737] to-transparent" />

        <div className="w-full max-w-[900px] p-2 md:p-5 m-auto">
          <Gallery
            options={options}
            onChange={handleOnSelect}
            activeIndex={form?.selectedIndexHistory}
            {...galleryProps}
          />
        </div>
      </>
    );
  }

  return (
    <div className="flex items-center gap-ds-16 md:gap-ds-20 w-full max-w-[900px] m-auto p-1">
      {withArrows && !isSolo && (
        <button
          type="button"
          className={cn(
            'text-left flex h-[60px] p-ds-8 bg-light hover:bg-dark border border-default rounded -mt-14',
            {
              '!opacity-20': isFirstIndex,
            }
          )}
          onClick={() => handleOnArrowsClick('LEFT')}
          disabled={isFirstIndex}
        >
          <FaChevronLeft className="h-3 w-3 m-auto" />
        </button>
      )}

      <div className="flex flex-col gap-ds-16 md:gap-ds-20 flex-1">
        {!isSolo ? (
          <div className="h-[200px] md:h-[480px] w-full bg-primitive-black rounded overflow-hidden">
            <img
              className="object-contain w-full h-full"
              alt="lightbox-selected"
              src={hasValue?.url}
            />
          </div>
        ) : (
          <img
            className="object-contain h-[200px] md:h-[480px] w-full rounded"
            alt="lightbox-selected"
            src={hasValue?.url}
          />
        )}

        {withBar && (
          <div
            className={cn(
              'bg-primitive-black text-primitive-white rounded-md md:rounded-lg p-ds-12 md:p-ds-16',
              {
                '!py-ds-8 md:!py-ds-12': barProps?.withSelect,
              }
            )}
          >
            <Bar
              onViewGallery={handleOnViewGallery}
              fileName={hasValue?.fileName}
              fileSize={hasValue?.fileSize}
              fileUrl={hasValue?.url}
              onDownload={onDownload}
              onDelete={onDelete}
              onClose={onClose}
              selectedIndex={form?.selectedIndex}
              {...barProps}
              onSelect={handleOnBarSelect}
            />
          </div>
        )}
      </div>

      {withArrows && !isSolo && (
        <button
          type="button"
          className={cn(
            'text-left flex h-[60px] p-ds-8 bg-light hover:bg-dark border border-default rounded -mt-14',
            {
              '!opacity-20': isLastIndex,
            }
          )}
          onClick={() => handleOnArrowsClick('RIGHT')}
          disabled={isLastIndex}
        >
          <FaChevronRight className="h-3 w-3 m-auto" />
        </button>
      )}
    </div>
  );
}

PhotoGallery.defaultProps = {
  options: [],
  selectedIndex: null,
  onDownload: false,
  onDelete: () => {},
  onClose: () => {},
  onSelect: () => {},
  barProps: {},
  galleryProps: {},
  withBar: true,
  withArrows: true,
  isSolo: false,
};

PhotoGallery.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      fileSize: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  selectedIndex: PropTypes.number,
  isSolo: PropTypes.bool,
  onDownload: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  onDelete: PropTypes.instanceOf(Function),
  onSelect: PropTypes.instanceOf(Function),
  onClose: PropTypes.instanceOf(Function),
  barProps: PropTypes.instanceOf(Object),
  galleryProps: PropTypes.instanceOf(Object),
  withBar: PropTypes.bool,
  withArrows: PropTypes.bool,
};

export default PhotoGallery;
